import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'webp' | 'jpeg';
}

interface UploadOptions {
  bucketPath?: string;
  generateThumbnail?: boolean;
  compressionOptions?: CompressionOptions;
}

export function useOptimizedImageUpload() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  // Detect WebP support
  const supportsWebP = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  };

  // Intelligent compression based on usage type
  const getCompressionSettings = (bucketPath: string = 'avatar'): CompressionOptions => {
    const defaults = {
      avatar: { maxWidth: 600, maxHeight: 600, quality: 0.925 },
      banner: { maxWidth: 1200, maxHeight: 600, quality: 0.9 },
      document: { maxWidth: 800, maxHeight: 800, quality: 0.85 },
      thumbnail: { maxWidth: 150, maxHeight: 150, quality: 0.8 }
    };

    const setting = defaults[bucketPath as keyof typeof defaults] || defaults.avatar;
    return {
      ...setting,
      format: supportsWebP() ? 'webp' : 'jpeg'
    };
  };

  // Advanced compression with WebP support
  const compressImage = async (
    file: File, 
    options: CompressionOptions
  ): Promise<{ original: File; thumbnail?: File }> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        const { maxWidth = 1200, maxHeight = 1200, quality = 0.9, format = 'jpeg' } = options;
        
        // Calculate optimal dimensions
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > height) {
          if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
          }
        } else {
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
        }

        // Main image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
        
        canvas.toBlob((blob) => {
          if (!blob) {
            resolve({ original: file });
            return;
          }

          const optimizedFile = new File([blob], 
            file.name.replace(/\.[^/.]+$/, `.${format}`), {
            type: mimeType,
            lastModified: Date.now(),
          });

          // Generate thumbnail if needed
          if (options.maxWidth && options.maxWidth > 150) {
            const thumbCanvas = document.createElement('canvas');
            const thumbCtx = thumbCanvas.getContext('2d')!;
            const thumbSize = 150;
            const thumbAspect = Math.min(thumbSize / width, thumbSize / height);
            
            thumbCanvas.width = width * thumbAspect;
            thumbCanvas.height = height * thumbAspect;
            thumbCtx.drawImage(img, 0, 0, thumbCanvas.width, thumbCanvas.height);

            thumbCanvas.toBlob((thumbBlob) => {
              let thumbnail: File | undefined;
              if (thumbBlob) {
                thumbnail = new File([thumbBlob], 
                  `thumb_${file.name.replace(/\.[^/.]+$/, `.${format}`)}`, {
                  type: mimeType,
                  lastModified: Date.now(),
                });
              }
              resolve({ original: optimizedFile, thumbnail });
            }, mimeType, quality * 0.9);
          } else {
            resolve({ original: optimizedFile });
          }
        }, mimeType, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImage = async (
    file: File, 
    options: UploadOptions = {}
  ): Promise<{ url: string; thumbnailUrl?: string } | null> => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour uploader une image.",
        variant: "destructive",
      });
      return null;
    }

    // Validation
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un fichier image valide.",
        variant: "destructive",
      });
      return null;
    }

    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: "Erreur",
        description: "L'image doit faire moins de 15MB.",
        variant: "destructive",
      });
      return null;
    }

    setUploading(true);
    
    try {
      const { bucketPath = 'avatar', generateThumbnail = false } = options;
      const compressionSettings = {
        ...getCompressionSettings(bucketPath),
        ...options.compressionOptions
      };

      // Compress image
      const { original: compressedFile, thumbnail } = await compressImage(file, compressionSettings);
      
      // Generate unique filename
      const fileExt = compressionSettings.format;
      const fileName = `${user.id}/${bucketPath}-${Date.now()}.${fileExt}`;

      // Upload main image with optimized cache headers
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, compressedFile, {
          cacheControl: '31536000', // 1 year
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      let thumbnailUrl: string | undefined;

      // Upload thumbnail if generated
      if (thumbnail && generateThumbnail) {
        const thumbFileName = `${user.id}/thumb_${bucketPath}-${Date.now()}.${fileExt}`;
        const { data: thumbData, error: thumbError } = await supabase.storage
          .from('avatars')
          .upload(thumbFileName, thumbnail, {
            cacheControl: '31536000',
            upsert: true
          });

        if (!thumbError) {
          const { data: { publicUrl: thumbPublicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(thumbData.path);
          thumbnailUrl = thumbPublicUrl;
        }
      }

      toast({
        title: "Image optimisée",
        description: "Votre image a été compressée et uploadée avec succès.",
      });

      return { url: publicUrl, thumbnailUrl };

    } catch (error: any) {
      console.error('Erreur upload:', error);
      toast({
        title: "Erreur d'upload",
        description: error.message || "Impossible d'uploader l'image.",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadImage,
    uploading,
    supportsWebP: supportsWebP()
  };
}